import { useState, useEffect } from "react";
import { useAuth } from "src/context/AuthProvider";
import { apiRequest } from "src/services/apiClient";
import { normalizeIban } from "src/helpers/sanitizeInput";

export type SendMethod = "email" | "post";
export type LegalMode = "self" | "api";
export type FinalSendMode = "email" | "letter" | "both";
export type DunningLevelSendModes = {
    friendly:  FinalSendMode;
    demanding: FinalSendMode;
    final:     FinalSendMode;
    legal:     FinalSendMode;
};

export type LevelDispatch = "" | "r1" | "r2";
export type DunningLetterOptions = {
    friendly:  LevelDispatch;
    demanding: LevelDispatch;
    final:     LevelDispatch;
    legal:     LevelDispatch;
};

export type DunningFines = {
    friendly:  number;
    demanding: number;
    final:     number;
    legal:     number;
};

export type PdfSettings = {
    showQrCode:             boolean;
    showExams:              boolean;
    showUstgNote:           boolean;
    headerLogoSlot:      "none" | "left" | "center" | "right";
    headerLogo:          string;
    headerLogoSize:      number;
    headerLogoOffsetX:   number;
    headerLogoOffsetY:   number;
    headerLeft:          string;
    headerCenter:        string;
    headerRight:         string;
    headerLeftFontColor:   string;
    headerLeftFontSize:    number;
    headerLeftFontWeight:  "normal" | "bold";
    headerLeftAlign:       "left" | "center" | "right";
    headerLeftVAlign:      "top" | "middle" | "bottom";
    headerCenterFontColor: string;
    headerCenterFontSize:  number;
    headerCenterFontWeight:"normal" | "bold";
    headerCenterAlign:     "left" | "center" | "right";
    headerCenterVAlign:    "top" | "middle" | "bottom";
    headerRightFontColor:  string;
    headerRightFontSize:   number;
    headerRightFontWeight: "normal" | "bold";
    headerRightAlign:      "left" | "center" | "right";
    headerRightVAlign:     "top" | "middle" | "bottom";
    headerBorderColor:     string;
    headerBorderWidth:     number;
    headerPaddingH:        number;
    headerPaddingV:        number;
    headerMarginBottom:    number;
    headerRepeatOnEveryPage: boolean;
    footerLeft:          string;
    footerCenter:        string;
    footerRight:         string;
    footerShowBank:      boolean;
    footerShowAddress:   boolean;
    footerLeftFontColor:   string;
    footerLeftFontSize:    number;
    footerLeftFontWeight:  "normal" | "bold";
    footerLeftAlign:       "left" | "center" | "right";
    footerLeftVAlign:      "top" | "middle" | "bottom";
    footerCenterFontColor: string;
    footerCenterFontSize:  number;
    footerCenterFontWeight:"normal" | "bold";
    footerCenterAlign:     "left" | "center" | "right";
    footerCenterVAlign:    "top" | "middle" | "bottom";
    footerRightFontColor:  string;
    footerRightFontSize:   number;
    footerRightFontWeight: "normal" | "bold";
    footerRightAlign:      "left" | "center" | "right";
    footerRightVAlign:     "top" | "middle" | "bottom";
    footerBorderColor:     string;
    footerBorderWidth:     number;
    footerPaddingH:        number;
    footerPaddingV:        number;
    footerMarginTop:       number;
    bodyHeading:           string;
    bodyHeadingBold:       boolean;
    bodyHeadingItalic:     boolean;
    bodyHeadingUnderline:  boolean;
    bodyChargeLine:        string;
    bodyPaymentTerms:      string;
};

export const DEFAULT_PDF_SETTINGS: PdfSettings = {
    showQrCode:             true,
    showExams:              true,
    showUstgNote:           false,
    headerLogoSlot:     "none",
    headerLogo:         "",
    headerLogoSize:     34,
    headerLogoOffsetX:  0,
    headerLogoOffsetY:  0,
    headerLeft:         "",
    headerCenter:       "",
    headerRight:        "",
    headerLeftFontColor:    "#555555",
    headerLeftFontSize:     7.5,
    headerLeftFontWeight:   "normal",
    headerLeftAlign:        "left",
    headerLeftVAlign:       "top",
    headerCenterFontColor:  "#555555",
    headerCenterFontSize:   7.5,
    headerCenterFontWeight: "normal",
    headerCenterAlign:      "center",
    headerCenterVAlign:     "top",
    headerRightFontColor:   "#555555",
    headerRightFontSize:    7.5,
    headerRightFontWeight:  "normal",
    headerRightAlign:       "right",
    headerRightVAlign:      "top",
    headerBorderColor:      "",
    headerBorderWidth:      1,
    headerPaddingH:         0,
    headerPaddingV:         0,
    headerMarginBottom:     0,
    headerRepeatOnEveryPage: false,
    footerLeft:         "",
    footerCenter:       "",
    footerRight:        "",
    footerShowBank:     false,
    footerShowAddress:  false,
    footerLeftFontColor:    "#555555",
    footerLeftFontSize:     7,
    footerLeftFontWeight:   "normal",
    footerLeftAlign:        "left",
    footerLeftVAlign:       "top",
    footerCenterFontColor:  "#555555",
    footerCenterFontSize:   7,
    footerCenterFontWeight: "normal",
    footerCenterAlign:      "center",
    footerCenterVAlign:     "top",
    footerRightFontColor:   "#555555",
    footerRightFontSize:    7,
    footerRightFontWeight:  "normal",
    footerRightAlign:       "right",
    footerRightVAlign:      "top",
    footerBorderColor:      "",
    footerBorderWidth:      1,
    footerPaddingH:         0,
    footerPaddingV:         0,
    footerMarginTop:        0,
    bodyHeading:            "",
    bodyHeadingBold:        true,
    bodyHeadingItalic:      true,
    bodyHeadingUnderline:   false,
    bodyChargeLine:         "",
    bodyPaymentTerms:       "",
};

export type EmailTexts = {
    invoice: string;
    resent: string;
    friendly: string;
    demanding: string;
    final: string;
    questionnaire: string;
};

export type LetterTexts = {
    invoice: string;
    friendly: string;
    demanding: string;
    final: string;
    legal: string;
};

export type DatevFields = {
    beraterNr: string;
    mandantNr: string;
    accountLength: number;
    revenueAccount: string;
    debtorAccount: string;
};

export type LetterQrCodes = {
    invoice: boolean;
    friendly: boolean;
    demanding: boolean;
    final: boolean;
    legal: boolean;
};

export type ReportIntervalUnit = "days" | "weeks" | "months";
export type ReportMonthDay = "first" | "last";

export type EmailSubjects = {
    invoice: string;
    resent: string;
    friendly: string;
    demanding: string;
    final: string;
    questionnaire: string;
};

export function useSettings() {
    const { user } = useAuth();

    const toTelArray = (v: unknown): string[] => {
        if (Array.isArray(v)) return v.map((x) => String(x ?? "").trim()).filter(Boolean);
        if (typeof v === "string")
            return v
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
        return [];
    };

    const [dunningDays, setDunningDays] = useState<number[]>([
        0, 7, 14, 21, 28,
    ]);

    const praxisStreet = user?.praxis?.street ?? "";

    const [praxis, setPraxis] = useState({
        name: user?.praxis?.name ?? "",
        specialization: (user?.praxis as any)?.specialization ?? [],
        street: praxisStreet,
        plz: user?.praxis?.plz ?? "",
        city: user?.praxis?.city ?? "",
        email: user?.praxis?.email ?? "",
        tel: toTelArray((user?.praxis as any)?.tel),
        logo: user?.praxis?.logo ?? "",
    });

    const [doctor, setDoctor] = useState({
        salutation: user?.doctor?.salutation ?? "",
        title:      user?.doctor?.title      ?? "",
        firstName:  user?.doctor?.firstName  ?? "",
        lastName:   user?.doctor?.lastName   ?? "",
        email:      user?.doctor?.email      ?? "",
        phone:      Array.isArray((user?.doctor as any)?.tel) ? (((user?.doctor as any)?.tel ?? [])[0] ?? "") : (((user?.doctor as any)?.tel as string | undefined) ?? ""),
        avatar:     user?.doctor?.avatar     ?? "",
        specialty:  user?.doctor?.specialty  ?? [],
        invoiceSenderName: user?.doctor?.invoiceSenderName ?? "",
        street:     user?.doctor?.street ?? "",
        plz:        user?.doctor?.plz    ?? "",
        city:       user?.doctor?.city   ?? "",
    });

    const [invoiceFormat, setInvoiceFormat] = useState<string>("A4 Portrait");
    const [sendMethods, setSendMethods] = useState<SendMethod[]>(["email"]);
    const [emailLinkModes, setEmailLinkModes] = useState<string[]>(["login", "sms_otp"]);
    const [postSendOnUpload, setPostSendOnUpload] = useState(false);
    const [emailIncludeText, setEmailIncludeText] = useState<boolean>(false);
    const [paidThreshold, setPaidThreshold] = useState<number>(2);
    const [sendOnUpload, setSendOnUpload] = useState<boolean>(true);
    const [sendOnQuestionnaireSubmit, setSendOnQuestionnaireSubmit] = useState<boolean>(false);
    const [requireLegalAcceptance, setRequireLegalAcceptance] = useState<boolean>(true);
    const [questionnairePostOption, setQuestionnairePostOption] = useState<boolean>(false);
    const [questionsEnabled, setQuestionsEnabled] = useState<boolean>(true);
    const [tagsEnabled, setTagsEnabled] = useState<boolean>(false);
    const [groupsEnabled, setGroupsEnabled] = useState<boolean>(false);
    const [hideCategoryLabels, setHideCategoryLabels] = useState<boolean>(false);
    const [dunningLevelSendModes, setDunningLevelSendModes] = useState<DunningLevelSendModes>({
        friendly: "email", demanding: "email", final: "email", legal: "email",
    });
    const [dunningFallbackToLetter, setDunningFallbackToLetter] = useState(false);
    const [dunningLetterOptions, setDunningLetterOptions] = useState<DunningLetterOptions>({
        friendly: "", demanding: "", final: "", legal: "",
    });
    const [dunningFinesEnabled, setDunningFinesEnabled] = useState(false);
    const [dunningFines, setDunningFines] = useState<DunningFines>({ friendly: 0, demanding: 0, final: 0, legal: 0 });

    const [bank, setBank] = useState({
        iban: normalizeIban(user?.doctor?.iban ?? ""),
        bic: user?.doctor?.bic ?? "",
        taxNumber: user?.doctor?.taxNumber ?? "",
        scanPayName: (user?.doctor as any)?.scanPayName ?? "",
    });

    const [sepa, setSepa] = useState({
        iban:          normalizeIban((user?.praxis as any)?.iban  ?? ""),
        bic:           (user?.praxis as any)?.bic   ?? "",
        accountHolder: (user?.praxis as any)?.name  ?? "",
    });

    const [bdtPath, setBdtPath] = useState<string>("");
    const [padPath, setPadPath] = useState<string>("");

    const [datev, setDatev] = useState<DatevFields>({
        beraterNr: "", mandantNr: "", accountLength: 4, revenueAccount: "", debtorAccount: "",
    });

    const [smtp, setSmtp] = useState({
        smtpHost:     "",
        smtpPort:     587,
        smtpSecure:   false,
        smtpUser:     "",
        smtpKey:      "",
        smtpFrom:     "",
        smtpFromName: "",
        smtpEnabled:  true,
    });

    const [emailTexts, setEmailTexts] = useState<EmailTexts>({
        invoice: "",
        resent: "",
        friendly: "",
        demanding: "",
        final: "",
        questionnaire: "",
    });

    const [letterTexts, setLetterTexts] = useState<LetterTexts>({
        invoice: "",
        friendly: "",
        demanding: "",
        final: "",
        legal: "",
    });

    const [letterQrCodes, setLetterQrCodes] = useState<LetterQrCodes>({
        invoice: false,
        friendly: false,
        demanding: false,
        final: false,
        legal: false,
    });

    const [emailSubjects, setEmailSubjects] = useState<EmailSubjects>({
        invoice: "",
        resent: "",
        friendly: "",
        demanding: "",
        final: "",
        questionnaire: "",
    });

    const [legalMode, setLegalMode] = useState<LegalMode>("self");
    const [paywiseApiKey, setPaywiseApiKey] = useState("");
    const [paywiseConfirmed, setPaywiseConfirmed] = useState(false);

    const [pdfSettings, setPdfSettings] = useState<PdfSettings>(DEFAULT_PDF_SETTINGS);
    // Whether the values above are this doctor's own saved customization, or just the
    // praxis-wide default they're currently inheriting (see settingsController.getSettings).
    const [pdfSettingsIsOwn, setPdfSettingsIsOwn] = useState(false);

    const [postColor, setPostColor]         = useState(false);
    const [postDuplex, setPostDuplex]       = useState(false);
    const [postDispatch, setPostDispatch]   = useState("");
    const [postEnvelope, setPostEnvelope]   = useState("DIN_LANG_FENSTER_LINKS");
    const [postShipping, setPostShipping]   = useState("national");

    const [activityDays, setActivityDays] = useState<number>(3);
    const [reportIntervalAmount, setReportIntervalAmount] = useState<number>(1);
    const [reportIntervalUnit, setReportIntervalUnit] = useState<ReportIntervalUnit>("months");
    const [reportWeekday, setReportWeekday] = useState<number>(1);
    const [reportMonthDay, setReportMonthDay] = useState<ReportMonthDay>("first");

    const [csvReportEnabled,        setCsvReportEnabled]        = useState(false);
    const [csvReportEmails,         setCsvReportEmails]         = useState<string[]>([]);
    const [csvReportIntervalAmount, setCsvReportIntervalAmount] = useState(1);
    const [csvReportIntervalUnit,   setCsvReportIntervalUnit]   = useState<"days" | "weeks" | "months">("months");
    const [csvReportFilter,         setCsvReportFilter]         = useState<"all" | "paid" | "paid_partial">("all");
    const [csvReportAmountMin,      setCsvReportAmountMin]      = useState(0);

    const [saved, setSaved] = useState<Record<string, boolean>>({});
    const [dirty, setDirty] = useState<Record<string, boolean>>({});

    useEffect(() => {
        apiRequest("/settings", { method: "GET" }).then((data: any) => {
            if (!data) return;
            if (data.scanPayName != null) setBank(prev => ({ ...prev, scanPayName: data.scanPayName }));
            if (data.sepaAccountHolder != null) setSepa(prev => ({ ...prev, accountHolder: data.sepaAccountHolder }));
            if (data.paidThreshold != null) setPaidThreshold(data.paidThreshold);
            if (data.invoiceFormat)         setInvoiceFormat(data.invoiceFormat);
            if (data.sendMethods?.length)   setSendMethods(data.sendMethods);
            if (data.emailLinkModes?.length) setEmailLinkModes(data.emailLinkModes);
            if (data.postSendOnUpload != null) setPostSendOnUpload(data.postSendOnUpload);
            if (data.sendOnUpload != null)  setSendOnUpload(data.sendOnUpload);
            if (data.sendOnQuestionnaireSubmit != null) setSendOnQuestionnaireSubmit(data.sendOnQuestionnaireSubmit);
            if (data.requireLegalAcceptance != null) setRequireLegalAcceptance(data.requireLegalAcceptance);
            if (data.questionnairePostOption != null) setQuestionnairePostOption(data.questionnairePostOption);
            if (data.questionsEnabled != null) setQuestionsEnabled(data.questionsEnabled);
            if (data.tagsEnabled != null) setTagsEnabled(data.tagsEnabled);
            if (data.groupsEnabled != null) setGroupsEnabled(data.groupsEnabled);
            if (data.hideCategoryLabels != null) setHideCategoryLabels(data.hideCategoryLabels);
            if (data.dunningIntervals?.length) {
                // Convert relative intervals to cumulative days for the UI
                const cumulative = data.dunningIntervals.reduce((acc: number[], v: number, i: number) =>
                    i === 0 ? [0] : [...acc, acc[acc.length - 1] + v], [] as number[]);
                setDunningDays(cumulative);
            }
            if (data.legalMode)            setLegalMode(data.legalMode);
            if (data.paywiseApiKey)        setPaywiseApiKey(data.paywiseApiKey);
            if (data.paywiseConfirmed)     setPaywiseConfirmed(data.paywiseConfirmed);
            if (data.dunningLevelSendModes) {
                setDunningLevelSendModes({
                    friendly:  data.dunningLevelSendModes.friendly  ?? "email",
                    demanding: data.dunningLevelSendModes.demanding ?? "email",
                    // back-compat: seed from legacy finalSendMode if new field absent
                    final:     data.dunningLevelSendModes.final     ?? data.finalSendMode ?? "email",
                    legal:     data.dunningLevelSendModes.legal     ?? "email",
                });
            } else if (data.finalSendMode) {
                setDunningLevelSendModes(prev => ({ ...prev, final: data.finalSendMode }));
            }
            if (data.dunningFallbackToLetter != null) setDunningFallbackToLetter(data.dunningFallbackToLetter);
            if (data.dunningLetterOptions) {
                setDunningLetterOptions({
                    friendly:  data.dunningLetterOptions.friendly  ?? "",
                    demanding: data.dunningLetterOptions.demanding ?? "",
                    final:     data.dunningLetterOptions.final     ?? "",
                    legal:     data.dunningLetterOptions.legal     ?? "",
                });
            }
            if (data.dunningFinesEnabled != null) setDunningFinesEnabled(data.dunningFinesEnabled);
            if (data.dunningFines) setDunningFines({
                friendly:  data.dunningFines.friendly  ?? 0,
                demanding: data.dunningFines.demanding ?? 0,
                final:     data.dunningFines.final     ?? 0,
                legal:     data.dunningFines.legal     ?? 0,
            });
            if (data.bdtPath != null) setBdtPath(data.bdtPath);
            if (data.padPath != null) setPadPath(data.padPath);
            if (data.datev) setDatev({
                beraterNr:      data.datev.beraterNr      ?? "",
                mandantNr:      data.datev.mandantNr      ?? "",
                accountLength:  data.datev.accountLength  ?? 4,
                revenueAccount: data.datev.revenueAccount ?? "",
                debtorAccount:  data.datev.debtorAccount  ?? "",
            });
            if (data.smtpHost != null) setSmtp({
                smtpHost:     data.smtpHost     ?? "",
                smtpPort:     data.smtpPort     ?? 587,
                smtpSecure:   data.smtpSecure   ?? false,
                smtpUser:     data.smtpUser     ?? "",
                smtpKey:      data.smtpKey      ?? "",
                smtpFrom:     data.smtpFrom     ?? "",
                smtpFromName: data.smtpFromName ?? "",
                smtpEnabled:  data.smtpEnabled  ?? true,
            });
            if (data.emailTexts) setEmailTexts({
                invoice:       data.emailTexts.invoice       ?? "",
                resent:        data.emailTexts.resent        ?? "",
                friendly:      data.emailTexts.friendly      ?? "",
                demanding:     data.emailTexts.demanding     ?? "",
                final:         data.emailTexts.final         ?? "",
                questionnaire: data.emailTexts.questionnaire ?? "",
            });
            if (data.emailSubjects) setEmailSubjects({
                invoice:       data.emailSubjects.invoice       ?? "",
                resent:        data.emailSubjects.resent        ?? "",
                friendly:      data.emailSubjects.friendly      ?? "",
                demanding:     data.emailSubjects.demanding     ?? "",
                final:         data.emailSubjects.final         ?? "",
                questionnaire: data.emailSubjects.questionnaire ?? "",
            });
            if (data.postColor    != null) setPostColor(data.postColor);
            if (data.postDuplex   != null) setPostDuplex(data.postDuplex);
            if (data.postDispatch) setPostDispatch(["r1", "r2"].includes(data.postDispatch) ? data.postDispatch : "");
            if (data.postEnvelope)         setPostEnvelope(data.postEnvelope);
            if (data.postShipping)         setPostShipping(data.postShipping);
            if (data.letterTexts) setLetterTexts({
                invoice:  data.letterTexts.invoice   ?? "",
                friendly: data.letterTexts.friendly  ?? "",
                demanding:data.letterTexts.demanding ?? "",
                final:    data.letterTexts.final     ?? "",
                legal:    data.letterTexts.legal     ?? "",
            });
            if (data.letterQrCodes) setLetterQrCodes({
                invoice:  !!data.letterQrCodes.invoice,
                friendly: !!data.letterQrCodes.friendly,
                demanding:!!data.letterQrCodes.demanding,
                final:    !!data.letterQrCodes.final,
                legal:    !!data.letterQrCodes.legal,
            });
            if (data.activityDays != null) setActivityDays(data.activityDays);
            if (data.reportIntervalAmount != null) setReportIntervalAmount(data.reportIntervalAmount);
            if (data.reportIntervalUnit)           setReportIntervalUnit(data.reportIntervalUnit);
            if (data.reportWeekday  != null)       setReportWeekday(data.reportWeekday);
            if (data.reportMonthDay)               setReportMonthDay(data.reportMonthDay);
            if (data.csvReportEnabled        != null) setCsvReportEnabled(data.csvReportEnabled);
            if (Array.isArray(data.csvReportEmails))  setCsvReportEmails(data.csvReportEmails);
            if (data.csvReportIntervalAmount != null) setCsvReportIntervalAmount(data.csvReportIntervalAmount);
            if (data.csvReportIntervalUnit)           setCsvReportIntervalUnit(data.csvReportIntervalUnit);
            if (data.csvReportFilter)                 setCsvReportFilter(data.csvReportFilter);
            if (data.csvReportAmountMin      != null) setCsvReportAmountMin(data.csvReportAmountMin);
            if (data.pdfSettings) {
                const defined = Object.fromEntries(
                    Object.entries(data.pdfSettings).filter(([, v]) => v != null),
                );
                setPdfSettings({ ...DEFAULT_PDF_SETTINGS, ...defined });
            }
            setPdfSettingsIsOwn(!!data.pdfSettingsIsOwn);
        }).catch(() => {/* use defaults */});
    }, []);

    const markDirty = (key: string) => setDirty((d) => ({ ...d, [key]: true }));
    const clearDirty = (key: string) => setDirty((d) => ({ ...d, [key]: false }));

    const markSaved = (key: string) => {
        setSaved((s) => ({ ...s, [key]: true }));
        setTimeout(() => setSaved((s) => ({ ...s, [key]: false })), 2000);
    };

    const save = async (key: string, payload: Record<string, unknown>): Promise<boolean> => {
        try {
            await apiRequest(
                `/settings/${key}`,
                { method: "PUT", body: JSON.stringify(payload) },
                { "Content-Type": "application/json" },
            );
            markSaved(key);
            clearDirty(key);
            return true;
        } catch (e) {
            console.error(`Failed to save ${key}`, e);
            return false;
        }
    };

    const toggleSendMethod = (method: SendMethod) =>
        setSendMethods((prev) =>
            prev.includes(method)
                ? prev.filter((m) => m !== method)
                : [...prev, method],
        );

    return {
        dunningDays,
        setDunningDays,
        praxis,
        setPraxis,
        doctor,
        setDoctor,
        invoiceFormat,
        setInvoiceFormat,
        sendMethods,
        toggleSendMethod,
        emailLinkModes,
        setEmailLinkModes,
        postSendOnUpload,
        setPostSendOnUpload,
        emailIncludeText,
        setEmailIncludeText,
        paidThreshold,
        setPaidThreshold,
        sendOnUpload,
        setSendOnUpload,
        sendOnQuestionnaireSubmit,
        setSendOnQuestionnaireSubmit,
        requireLegalAcceptance,
        setRequireLegalAcceptance,
        questionnairePostOption,
        setQuestionnairePostOption,
        questionsEnabled,
        setQuestionsEnabled,
        tagsEnabled,
        setTagsEnabled,
        groupsEnabled,
        setGroupsEnabled,
        hideCategoryLabels,
        setHideCategoryLabels,
        dunningLevelSendModes,
        setDunningLevelSendModes,
        dunningFallbackToLetter,
        setDunningFallbackToLetter,
        dunningLetterOptions,
        setDunningLetterOptions,
        dunningFinesEnabled,
        setDunningFinesEnabled,
        dunningFines,
        setDunningFines,
        bank,
        setBank,
        sepa,
        setSepa,
        bdtPath,
        setBdtPath,
        padPath,
        setPadPath,
        datev,
        setDatev,
        smtp,
        setSmtp,
        emailTexts,
        setEmailTexts,
        emailSubjects,
        setEmailSubjects,
        legalMode,
        setLegalMode,
        paywiseApiKey,
        setPaywiseApiKey,
        paywiseConfirmed,
        setPaywiseConfirmed,
        pdfSettings,
        setPdfSettings,
        pdfSettingsIsOwn,
        setPdfSettingsIsOwn,
        postColor, setPostColor,
        postDuplex, setPostDuplex,
        postDispatch, setPostDispatch,
        postEnvelope, setPostEnvelope,
        postShipping, setPostShipping,
        letterTexts, setLetterTexts,
        letterQrCodes, setLetterQrCodes,
        activityDays,
        setActivityDays,
        reportIntervalAmount,
        setReportIntervalAmount,
        reportIntervalUnit,
        setReportIntervalUnit,
        reportWeekday,
        setReportWeekday,
        reportMonthDay,
        setReportMonthDay,
        csvReportEnabled,        setCsvReportEnabled,
        csvReportEmails,         setCsvReportEmails,
        csvReportIntervalAmount, setCsvReportIntervalAmount,
        csvReportIntervalUnit,   setCsvReportIntervalUnit,
        csvReportFilter,         setCsvReportFilter,
        csvReportAmountMin,      setCsvReportAmountMin,
        saved,
        save,
        markSaved,
        dirty,
        markDirty,
        clearDirty,
    };
}
